import { useState, useRef, useEffect, useCallback } from "react";
import ConfirmModal from "../../ConfirmModal.jsx";
import { useReportTemplateContext } from "../ReportTemplate/ReportTemplateContext";
import { getDynamicPTS, saveDynamicPTS } from "../../../api/api";
import { isProd } from "../../../devprodConfig";

function findLinkedProjectId(sections) {
  for (const section of sections || []) {
    let content = section.content;
    if (typeof content === 'string') { try { content = JSON.parse(content); } catch { continue; } }
    for (const block of content?.blocks || []) {
      if (block.type === 'SubsectionBuilderBlock' && block.data?.linkedProjectId)
        return block.data.linkedProjectId;
    }
  }
  return null;
}

const newBullet = () => ({ id: crypto.randomUUID(), text: "" });
const newSection = () => ({
  id: crypto.randomUUID(),
  label: "",
  bullets: [newBullet()],
  costLabel: "Karkea kustannusarvio",
  costMin: "",
  costMax: "",
  costUnit: "",
  totalCostMin: "",
  totalCostMax: "",
  lifecycle: "",
});
const newOption = () => ({
  id: crypto.randomUUID(),
  title: "",
  sections: [newSection()],
});

export default function VaihtoehdotBlock({ data, onChange }) {
  const [options, setOptions] = useState(() =>
    data?.options?.length ? data.options : [newOption()]
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingDeleteRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const { sections: reportSections } = useReportTemplateContext();
  const [ptsSections, setPtsSections] = useState([]);
  const [activePicker, setActivePicker] = useState(null);
  const [pickerSection, setPickerSection] = useState('');
  const [addingPTS, setAddingPTS] = useState(false);

  const linkedProjectId = findLinkedProjectId(reportSections);

  useEffect(() => {
    if (!linkedProjectId) return;
    getDynamicPTS(linkedProjectId)
      .then(res => {
        const secs = res.data.sections ?? [];
        setPtsSections(secs);
        // Reconcile: clear pts fields from options whose entry no longer exists in PTS
        const currentOpts = dataRef.current?.options ?? [];
        if (currentOpts.some(o => o.ptsSectionName)) {
          const reconciled = currentOpts.map(o => {
            if (!o.ptsSectionName) return o;
            const sec = secs.find(s => s.name === o.ptsSectionName);
            const stillExists = sec?.entries?.some(e => e.label === o.ptsAddedLabel);
            return stillExists ? o : { ...o, ptsSectionName: undefined, ptsAddedLabel: undefined };
          });
          if (reconciled.some((o, i) => o !== currentOpts[i])) {
            setOptions(reconciled);
            onChange?.({ ...dataRef.current, options: reconciled });
          }
        }
      })
      .catch(() => {});
  }, [linkedProjectId]);

  const handleRemoveFromPTS = async (optIdx) => {
    if (!linkedProjectId) return;
    const { ptsSectionName, ptsAddedLabel } = options[optIdx];
    setAddingPTS(true);
    try {
      const { data: ptsData } = await getDynamicPTS(linkedProjectId);
      // Strip DB fields and exclude the matching entry — backend does delete+reinsert per section
      const updated = (ptsData.sections ?? []).map(sec => ({
        ...sec,
        entries: (sec.entries ?? [])
          .filter(e => !(sec.name === ptsSectionName && e.label === ptsAddedLabel))
          .map(({ id, id_section, created_at, updated_at, ...rest }) => rest),
      }));
      await saveDynamicPTS(linkedProjectId, updated, ptsData.name);
      const next = options.map((o, i) =>
        i === optIdx ? { ...o, ptsSectionName: undefined, ptsAddedLabel: undefined } : o
      );
      setOptions(next);
      onChange?.({ ...dataRef.current, options: next });
    } catch (err) {
      console.warn('[VaihtoehdotBlock] remove from PTS failed:', err);
    } finally {
      setAddingPTS(false);
    }
  };

  const handleAddToPTS = async (optIdx) => {
    if (!linkedProjectId || !pickerSection) return;
    const label = options[optIdx].title.trim();
    if (!label) return;
    setAddingPTS(true);
    try {
      const { data: ptsData } = await getDynamicPTS(linkedProjectId);
      // Strip DB fields before sending — backend does delete+reinsert per section
      const updated = (ptsData.sections ?? []).map(sec => ({
        ...sec,
        entries: [
          ...(sec.entries ?? []).map(({ id, id_section, created_at, updated_at, ...rest }) => rest),
          ...(sec.name === pickerSection ? [{
            label,
            y1:'', y2:'', y3:'', y4:'', y5:'', y6:'', y7:'', y8:'', y9:'', y10:'', y11:'',
            sort_order: sec.entries?.length ?? 0,
          }] : []),
        ],
      }));
      await saveDynamicPTS(linkedProjectId, updated, ptsData.name);
      const { data: fresh } = await getDynamicPTS(linkedProjectId);
      setPtsSections(fresh.sections ?? []);
      const next = options.map((o, i) =>
        i === optIdx ? { ...o, ptsSectionName: pickerSection, ptsAddedLabel: label } : o
      );
      setOptions(next);
      onChange?.({ ...dataRef.current, options: next });
      setActivePicker(null);
      setPickerSection('');
    } catch (err) {
      console.warn('[VaihtoehdotBlock] add to PTS failed:', err);
    } finally {
      setAddingPTS(false);
    }
  };

  const push = (next) => {
    setOptions(next);
    onChange?.({ ...dataRef.current, options: next });
  };

  // ── option helpers ──────────────────────────────────────────
  const updateOptionTitle = (optIdx, value) =>
    push(options.map((o, i) => i === optIdx ? { ...o, title: value } : o));

  const addOption = () => push([...options, newOption()]);

  const removeOption = (optIdx) => {
    push(options.filter((_, i) => i !== optIdx));
  };

  // ── section helpers ─────────────────────────────────────────
  const updateSection = (optIdx, secIdx, field, value) =>
    push(options.map((o, i) =>
      i !== optIdx ? o : {
        ...o,
        sections: o.sections.map((s, j) =>
          j === secIdx ? { ...s, [field]: value } : s
        ),
      }
    ));

  const addSection = (optIdx) =>
    push(options.map((o, i) =>
      i !== optIdx ? o : { ...o, sections: [...o.sections, newSection()] }
    ));

  const removeSection = (optIdx, secIdx) => {
    push(options.map((o, i) =>
      i !== optIdx ? o : { ...o, sections: o.sections.filter((_, j) => j !== secIdx) }
    ));
  };

  // ── bullet helpers ──────────────────────────────────────────
  const updateBullet = (optIdx, secIdx, bIdx, value) =>
    push(options.map((o, i) =>
      i !== optIdx ? o : {
        ...o,
        sections: o.sections.map((s, j) =>
          j !== secIdx ? s : {
            ...s,
            bullets: s.bullets.map((b, k) => k === bIdx ? { ...b, text: value } : b),
          }
        ),
      }
    ));

  const addBullet = (optIdx, secIdx) =>
    push(options.map((o, i) =>
      i !== optIdx ? o : {
        ...o,
        sections: o.sections.map((s, j) =>
          j !== secIdx ? s : { ...s, bullets: [...s.bullets, newBullet()] }
        ),
      }
    ));

  const handleBulletPaste = (e, optIdx, secIdx, bIdx) => {
    const text = e.clipboardData.getData("text");
    const lines = text
      .split(/\r?\n/)
      .map(l => l.replace(/^[\s•\-\*•‣◦⁃]+/, "").trim())
      .filter(l => l.length > 0);
    if (lines.length <= 1) return; // single line — let default paste handle it
    e.preventDefault();
    push(options.map((o, i) => {
      if (i !== optIdx) return o;
      return {
        ...o,
        sections: o.sections.map((s, j) => {
          if (j !== secIdx) return s;
          const before = s.bullets.slice(0, bIdx);
          const after = s.bullets.slice(bIdx + 1);
          const newBullets = lines.map(text => ({ id: crypto.randomUUID(), text }));
          return { ...s, bullets: [...before, ...newBullets, ...after] };
        }),
      };
    }));
  };

  const removeBullet = (optIdx, secIdx, bIdx) => {
    push(options.map((o, i) =>
      i !== optIdx ? o : {
        ...o,
        sections: o.sections.map((s, j) =>
          j !== secIdx ? s : { ...s, bullets: s.bullets.filter((_, k) => k !== bIdx) }
        ),
      }
    ));
  };

  const DEV_OPTIONS = [
    {
      title: 'Ikkunoiden peruskorjaus',
      sections: [{
        label: 'Taloyhtiön vastuulla:',
        bullets: [
          'Puitteiden maalaus ja tiivisteiden uusiminen',
          'Saranoiden ja lukitusten huolto ja uusiminen',
          'Vesipellin uusiminen tarvittaessa',
        ],
        costMin: '500', costMax: '800', costUnit: 'ikkuna', totalCost: '150 000', lifecycle: '15',
      }],
    },
    {
      title: 'Ikkunoiden uusiminen',
      sections: [{
        label: 'Taloyhtiön vastuulla:',
        bullets: [
          'Kaikkien ikkunoiden uusiminen energiatehokkailla kolmilasisilla ikkunoilla',
          'Vanhojen ikkunoiden purku ja kierrätys',
          'Uusien ikkunoiden asennus ja tiivistys',
        ],
        costMin: '900', costMax: '1 300', costUnit: 'ikkuna', totalCost: '270 000', lifecycle: '30',
      }],
    },
    {
      title: 'Huoltomaalaus ja tiivisteiden uusiminen',
      sections: [{
        label: 'Taloyhtiön vastuulla:',
        bullets: [
          'Ikkunapuitteiden ja karmin huoltomaalaus',
          'Vanhentuneinen tiivisteiden uusiminen',
        ],
        costMin: '120', costMax: '200', costUnit: 'ikkuna', totalCost: '40 000', lifecycle: '8',
      }],
    },
  ];
  const devFillData = () => {
    const count = 2 + Math.floor(Math.random() * 2);
    const next = DEV_OPTIONS.slice(0, count).map(o => ({
      ...newOption(),
      title: o.title,
      sections: o.sections.map(s => ({
        ...newSection(),
        label: s.label,
        bullets: s.bullets.map(text => ({ id: crypto.randomUUID(), text })),
        costMin: s.costMin,
        costMax: s.costMax,
        costUnit: s.costUnit,
        totalCost: s.totalCost,
        lifecycle: s.lifecycle,
      })),
    }));
    push(next);
  };

  const askConfirm = useCallback((fn) => {
    pendingDeleteRef.current = fn;
    setShowConfirm(true);
  }, []);

  const handleConfirm = () => {
    pendingDeleteRef.current?.();
    pendingDeleteRef.current = null;
    setShowConfirm(false);
  };

  const handleCancelConfirm = () => {
    pendingDeleteRef.current = null;
    setShowConfirm(false);
  };

  return (
    <div className="d-flex flex-column gap-3 vaihtoehdot-block">
      <style>{`
        .vaihtoehdot-block input::placeholder { color: #bbb; opacity: 1; }
        .vaihtoehdot-cost-grid {
          display: grid;
          grid-template-columns: 190px 110px 80px 12px 80px 1fr;
          align-items: center;
          column-gap: 6px;
          row-gap: 6px;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        @media (max-width: 576px) {
          .vaihtoehdot-cost-grid {
            grid-template-columns: auto 1fr auto 1fr;
          }
          .vaihtoehdot-cost-grid .cost-label,
          .vaihtoehdot-cost-grid .cost-unit {
            grid-column: 1 / -1;
          }
          .vaihtoehdot-cost-grid .cost-prefix {
            text-align: left !important;
          }
        }
      `}</style>
      {options.map((opt, optIdx) => (
        <div key={opt.id} className="border rounded bg-white overflow-hidden">
          {/* Option header */}
          <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom border-dark border-2 bg-light">
            <span className="fw-bold text-nowrap" style={{ fontSize: "0.95rem" }}>
              Vaihtoehto {optIdx + 1}:
            </span>
            <input
              className="form-control form-control-sm fw-bold"
              value={opt.title}
              onChange={e => updateOptionTitle(optIdx, e.target.value)}
              placeholder="Otsikko (esim. Ikkunoiden kunnostaminen)"
            />
            {linkedProjectId && (() => {
              const isAdded = !!(opt.ptsSectionName && opt.ptsAddedLabel);
              if (isAdded) return (
                <button
                  className="btn btn-sm btn-success flex-shrink-0"
                  style={{ padding: '2px 7px', fontSize: '0.78rem' }}
                  title="Lisätty PTS:ään — klikkaa poistaaksesi"
                  onClick={() => handleRemoveFromPTS(optIdx)}
                  disabled={addingPTS}
                >✓ PTS</button>
              );
              if (activePicker === optIdx) return (
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                  <select
                    className="form-select form-select-sm"
                    style={{ fontSize: '0.78rem', minWidth: 120 }}
                    value={pickerSection}
                    onChange={e => setPickerSection(e.target.value)}
                  >
                    <option value="">— Osio —</option>
                    {ptsSections.map(s => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm btn-success flex-shrink-0"
                    style={{ padding: '2px 7px', fontSize: '0.78rem' }}
                    onClick={() => handleAddToPTS(optIdx)}
                    disabled={!pickerSection || addingPTS}
                  >{addingPTS ? '…' : '✓'}</button>
                  <button
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                    style={{ padding: '2px 7px', fontSize: '0.78rem' }}
                    onClick={() => { setActivePicker(null); setPickerSection(''); }}
                  >✕</button>
                </div>
              );
              return (
                <button
                  className="btn btn-sm btn-outline-secondary flex-shrink-0"
                  style={{ padding: '2px 7px', fontSize: '0.78rem' }}
                  title="Lisää PTS:ään"
                  onClick={() => { setActivePicker(optIdx); setPickerSection(''); }}
                >+ PTS</button>
              );
            })()}
            <button
              className="btn btn-outline-danger btn-sm flex-shrink-0"
              style={{ padding: "2px 7px" }}
              onClick={() => askConfirm(() => removeOption(optIdx))}
              disabled={options.length === 1}
            >✕</button>
          </div>

          {/* Sections */}
          {opt.sections.map((sec, secIdx) => (
            <div
              key={sec.id}
              className="px-3 pt-3 pb-2"
              style={{ borderBottom: secIdx < opt.sections.length - 1 ? "1px dashed #dee2e6" : "none" }}
            >
              {/* Section label */}
              <input
                className="form-control form-control-sm mb-2 fw-semibold"
                style={{ borderLeft: '3px solid #6c757d' }}
                value={sec.label}
                onChange={e => updateSection(optIdx, secIdx, "label", e.target.value)}
                placeholder="Osion otsikko (esim. Taloyhtiön vastuulle kuuluvat korjaukset:)"
              />

              {/* Bullets */}
              <div className="mb-2">
                {sec.bullets.map((b, bIdx) => (
                  <div key={b.id} className="d-flex align-items-center gap-2 mb-1">
                    <span className="text-secondary" style={{ fontSize: "1rem" }}>•</span>
                    <input
                      className="form-control form-control-sm flex-grow-1"
                      value={b.text}
                      onChange={e => updateBullet(optIdx, secIdx, bIdx, e.target.value)}
                      onPaste={e => handleBulletPaste(e, optIdx, secIdx, bIdx)}
                      placeholder="Yksi kohta tai liitä kokonainen lista"
                    />
                    <button
                      className="btn btn-outline-danger btn-sm flex-shrink-0"
                      style={{ padding: "1px 6px" }}
                      onClick={() => askConfirm(() => removeBullet(optIdx, secIdx, bIdx))}
                      disabled={sec.bullets.length === 1}
                    >✕</button>
                  </div>
                ))}
                <div className="d-flex align-items-center gap-2 mt-3">
                  <span style={{ fontSize: "1rem", visibility: "hidden" }}>•</span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    style={{ fontSize: "0.78rem" }}
                    onClick={() => addBullet(optIdx, secIdx)}
                  >+ Lisää kohta</button>
                </div>
              </div>

              {/* Karkea & Kokonaiskustannusarvio — grid for vertical alignment */}
              <div className="vaihtoehdot-cost-grid">
                <span className="cost-label" style={{ fontSize: "0.875rem" }}>Karkea kustannusarvio:</span>
                <span className="cost-prefix text-secondary text-nowrap" style={{ fontSize: "0.875rem", display: "block", textAlign: "right" }}>Keskimäärin n.</span>
                <input
                  className="form-control form-control-sm"
                  value={sec.costMin}
                  onChange={e => updateSection(optIdx, secIdx, "costMin", e.target.value)}
                  placeholder="Min"
                />
                <span className="text-secondary text-center">–</span>
                <input
                  className="form-control form-control-sm"
                  value={sec.costMax}
                  onChange={e => updateSection(optIdx, secIdx, "costMax", e.target.value)}
                  placeholder="Max"
                />
                <div className="cost-unit d-flex align-items-center gap-2">
                  <span className="text-secondary text-nowrap">€ /</span>
                  <input
                    className="form-control form-control-sm flex-grow-1"
                    value={sec.costUnit}
                    onChange={e => updateSection(optIdx, secIdx, "costUnit", e.target.value)}
                    placeholder="ikkuna, m², kpl..."
                  />
                </div>

                <span className="cost-label" style={{ fontSize: "0.875rem" }}>Kokonaiskustannusarvio:</span>
                <span className="cost-prefix text-secondary text-nowrap" style={{ fontSize: "0.875rem", display: "block", textAlign: "right" }}>n.</span>
                <input
                  className="form-control form-control-sm"
                  style={{ gridColumn: "span 3" }}
                  value={sec.totalCost ?? ""}
                  onChange={e => updateSection(optIdx, secIdx, "totalCost", e.target.value)}
                  placeholder="Kokonaiskustannus"
                />
                <span className="cost-unit text-secondary">€</span>
              </div>

              {/* Käyttöikäarvio */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-nowrap" style={{ fontSize: "0.875rem" }}>Käyttöikäarvio:</span>
                <span className="text-secondary fw-bold">&gt;</span>
                <input
                  className="form-control form-control-sm"
                  style={{ width: 80, flexShrink: 0 }}
                  value={sec.lifecycle}
                  onChange={e => updateSection(optIdx, secIdx, "lifecycle", e.target.value)}
                  placeholder="15"
                />
                <span className="text-secondary text-nowrap">vuotta</span>
              </div>

              {/* Remove section */}
              {opt.sections.length > 1 && (
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    style={{ fontSize: "0.78rem" }}
                    onClick={() => askConfirm(() => removeSection(optIdx, secIdx))}
                  >Poista osio</button>
                </div>
              )}
            </div>
          ))}

          {/* Add section */}
          <div className="d-flex justify-content-center py-2 border-top">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ fontSize: "0.78rem" }}
              onClick={() => addSection(optIdx)}
            >+ Lisää osio</button>
          </div>
        </div>
      ))}

      <div className="d-flex justify-content-center align-items-center gap-2">
        <button className="btn btn-outline-primary btn-sm" onClick={addOption}>
          + Lisää vaihtoehto
        </button>
        {/* Dev fill button hidden */}
      </div>

      <ConfirmModal
        show={showConfirm}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        title="Vahvista poisto"
        message="Haluatko varmasti poistaa tämän?"
        confirmText="Poista"
        cancelText="Peru"
      />
    </div>
  );
}
